import { __assign, __rest } from "tslib";
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
export var RemoteHookContext = createContext({
    subscribe: function () { return ({ id: '', unsubscribe: function () { return undefined; } }); },
    updateState: function () { return undefined; },
    getState: function () { return undefined; },
    registerHook: function () { return undefined; },
    updateArgs: function () { return undefined; },
    subscribeToArgs: function () { return function () { return undefined; }; },
});
// Fake component that executes the remote hook
function HookExecutor(_a) {
    var id = _a.id, hookFunction = _a.hookFunction, updateState = _a.updateState, initialArgs = _a.initialArgs;
    var subscribeToArgs = useContext(RemoteHookContext).subscribeToArgs;
    var _b = useState(initialArgs), args = _b[0], setArgs = _b[1];
    // Subscribe to argument changes
    useEffect(function () {
        var unsubscribe = subscribeToArgs(id, setArgs);
        return function () {
            unsubscribe();
        };
    }, [id, subscribeToArgs]);
    // Always call the hook with args (rules of hooks)
    var hookResult = hookFunction.apply(void 0, args);
    // Update state with the result
    useEffect(function () {
        // always set loading to false when we have a result
        updateState(id, { loading: false, hookResult: hookResult });
    }, [hookResult, id, updateState]);
    return null;
}
export var RemoteHookProvider = function (_a) {
    var children = _a.children;
    var state = useMemo(function () { return ({}); }, []);
    // React state to track available hooks (for re-rendering)
    var _b = useState({}), availableHooks = _b[0], setAvailableHooks = _b[1];
    // Mutable state for arguments (no re-renders)
    var argSubscriptions = useMemo(function () { return ({}); }, []);
    // Cleanup all subscriptions when provider unmounts
    useEffect(function () {
        return function () {
            // Clear state
            Object.keys(state).forEach(function (id) {
                delete state[id];
            });
            // Clear available hooks (this stops rendering HookExecutors)
            setAvailableHooks({});
            // Clear arg subscriptions
            Object.keys(argSubscriptions).forEach(function (id) {
                delete argSubscriptions[id];
            });
        };
    }, [state, argSubscriptions]);
    var subscribe = useCallback(function (notify) {
        var id = crypto.randomUUID();
        state[id] = { id: id, value: 0, notify: notify };
        return {
            id: id,
            unsubscribe: function () {
                delete state[id];
                // Also remove from availableHooks and hookArgs to stop rendering HookExecutor
                delete argSubscriptions[id];
                setAvailableHooks(function (prev) {
                    var _a = prev, _b = id, removed = _a[_b], rest = __rest(_a, [typeof _b === "symbol" ? _b : _b + ""]);
                    return rest;
                });
            },
        };
    }, [setAvailableHooks]);
    var updateState = useCallback(function (id, value) {
        var entry = state[id];
        if (!entry) {
            return;
        }
        // Merge with existing value
        entry.value = __assign(__assign({}, entry.value), value);
        entry.notify();
    }, []);
    var getState = useCallback(function (id) {
        var _a;
        return (_a = state[id]) === null || _a === void 0 ? void 0 : _a.value;
    }, []);
    var registerHook = useCallback(function (id, hookFunction) {
        setAvailableHooks(function (prev) {
            var _a;
            return (__assign(__assign({}, prev), (_a = {}, _a[id] = hookFunction, _a)));
        });
    }, [setAvailableHooks]);
    var updateArgs = useCallback(function (id, args) {
        if (!argSubscriptions[id]) {
            argSubscriptions[id] = { id: id, args: args, argNotifiers: new Set() };
        }
        else {
            argSubscriptions[id].args = args;
        }
        // Notify all arg subscribers for this ID
        argSubscriptions[id].argNotifiers.forEach(function (callback) {
            try {
                callback(args);
            }
            catch (err) {
                console.error('Error in arg subscriber callback:', err);
            }
        });
    }, [argSubscriptions]);
    var subscribeToArgs = useCallback(function (id, callback) {
        if (!argSubscriptions[id]) {
            argSubscriptions[id] = { id: id, args: [], argNotifiers: new Set() };
        }
        argSubscriptions[id].argNotifiers.add(callback);
        // Always call immediately with current args (even if empty)
        callback(argSubscriptions[id].args);
        return function () {
            var _a;
            (_a = argSubscriptions[id]) === null || _a === void 0 ? void 0 : _a.argNotifiers.delete(callback);
        };
    }, [argSubscriptions]);
    var contextValue = useMemo(function () { return ({ subscribe: subscribe, updateState: updateState, getState: getState, registerHook: registerHook, updateArgs: updateArgs, subscribeToArgs: subscribeToArgs }); }, [subscribe, updateState, getState, registerHook, updateArgs, subscribeToArgs]);
    return (React.createElement(RemoteHookContext.Provider, { value: contextValue },
        Object.keys(availableHooks).map(function (id) {
            var _a;
            var hookFunction = availableHooks[id];
            // Only render if we have both the hook function and the state entry
            if (!hookFunction || !state[id]) {
                return null;
            }
            // Get the initial args for this hook
            var initialArgs = ((_a = argSubscriptions[id]) === null || _a === void 0 ? void 0 : _a.args) || [];
            return React.createElement(HookExecutor, { key: id, id: id, hookFunction: hookFunction, updateState: updateState, initialArgs: initialArgs });
        }),
        children));
};
//# sourceMappingURL=remote-hook-provider.js.map